import { useState, useEffect, FormEvent } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { useBudget, useUpdateBudget } from '../hooks/useBudget';

function toNum(val?: number | null): string {
  if (val == null) return '';
  return String(val);
}

export default function BudgetSettings() {
  const { data: budget, isLoading, isError } = useBudget();
  const updateBudget = useUpdateBudget();

  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [perPurchaseLimit, setPerPurchaseLimit] = useState('');
  const [quickApprove, setQuickApprove] = useState('');
  const [manualCheckout, setManualCheckout] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (budget) {
      setMonthlyLimit(toNum(budget.monthly_limit));
      setPerPurchaseLimit(toNum(budget.per_purchase_limit));
      setQuickApprove(toNum(budget.quick_approve_threshold));
      setManualCheckout(toNum(budget.manual_checkout_threshold));
    }
  }, [budget]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    updateBudget.mutate(
      {
        monthly_limit: monthlyLimit ? parseFloat(monthlyLimit) : null,
        per_purchase_limit: perPurchaseLimit ? parseFloat(perPurchaseLimit) : null,
        quick_approve_threshold: quickApprove ? parseFloat(quickApprove) : null,
        manual_checkout_threshold: manualCheckout ? parseFloat(manualCheckout) : null,
      },
      { onSuccess: () => setSaved(true) }
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading budget...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-destructive">Failed to load budget settings.</p>
      </div>
    );
  }

  const spent = budget?.current_month_spent ?? 0;

  return (
    <form
      id="budget-settings"
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-card p-6 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-card-foreground">Budget Settings</h3>
        <span className="text-sm text-muted-foreground">
          Spent this month: <span className="font-medium text-foreground">${spent.toFixed(2)}</span>
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <label htmlFor="monthly-limit" className="text-sm font-medium text-foreground">
            Monthly Limit
          </label>
          <input
            id="monthly-limit"
            type="number"
            min="0"
            step="0.01"
            value={monthlyLimit}
            onChange={(e) => setMonthlyLimit(e.target.value)}
            placeholder="No limit"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="per-purchase" className="text-sm font-medium text-foreground">
            Per-Purchase Limit
          </label>
          <input
            id="per-purchase"
            type="number"
            min="0"
            step="0.01"
            value={perPurchaseLimit}
            onChange={(e) => setPerPurchaseLimit(e.target.value)}
            placeholder="No limit"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="quick-approve" className="text-sm font-medium text-foreground">
            Quick Approve Under
          </label>
          <input
            id="quick-approve"
            type="number"
            min="0"
            step="0.01"
            value={quickApprove}
            onChange={(e) => setQuickApprove(e.target.value)}
            placeholder="$50"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="manual-checkout" className="text-sm font-medium text-foreground">
            Manual Checkout Over
          </label>
          <input
            id="manual-checkout"
            type="number"
            min="0"
            step="0.01"
            value={manualCheckout}
            onChange={(e) => setManualCheckout(e.target.value)}
            placeholder="$200"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Purchases under Quick Approve get one-tap approval. Between Quick Approve and Manual Checkout require review. Over Manual Checkout you complete checkout yourself.
      </p>

      {updateBudget.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Failed to save budget. Please try again.
        </div>
      )}

      {saved && !updateBudget.isPending && (
        <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">
          Budget saved.
        </div>
      )}

      <button
        type="submit"
        disabled={updateBudget.isPending}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      >
        {updateBudget.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Save Budget
      </button>
    </form>
  );
}
